import {
  BodyText,
  Button,
  Caption,
  Card,
  Col,
  Container,
  Flex_unstable as Flex,
  Heading,
  QuestionAnswerIcon,
  RichIcon,
  Row,
  SearchIcon,
  Separator,
  TruncateText,
  WhatsappIcon,
} from '@farol-ds/react'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef } from 'react'

import { WHATSAPP_URL } from '../../constants'
import styles from './seus-direitos-cta-v2.module.scss'

export interface SeusDireitosCtaV2Props {
  searchTerm: string
  onRetrySearch: () => void
  experimentId?: string
  page?: string
  variant?: string | null
}

const WHATSAPP_MESSAGE_PREFIX: Record<string, string> = {
  variantA: 'Minha dúvida é: ',
  variantB: 'Quero entender: ',
}

const SEARCH_BUTTON_LABEL = 'Refazer busca'
const DEFAULT_WHATSAPP_MESSAGE = 'Olá! tenho uma dúvida jurídica'
const MESSAGE_PREVIEW_TEXT =
  'Com base nas informações públicas disponíveis e nas regras previstas na legislação brasileira, uma possível explicação para essa dúvida é...'

export function SeusDireitosCtaV2({
  searchTerm,
  onRetrySearch,
  experimentId,
  page,
  variant,
}: SeusDireitosCtaV2Props) {
  const isVariantB = variant === 'variantB'
  const whatsappButtonLabel = isVariantB ? 'Acessar resposta no WhatsApp' : 'Perguntar no WhatsApp'

  const whatsappRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLButtonElement>(null)
  const whatsappCtaViewSentRef = useRef(false)
  const searchCtaViewSentRef = useRef(false)
  const session = useUserSession()
  const isWhatsappCtaOnScreen = useOnScreen(whatsappRef, { threshold: 0.5 }, false)
  const isSearchCtaOnScreen = useOnScreen(searchRef, { threshold: 0.5 }, false)

  const whatsappMetricsPayload = useMemo(
    () => ({
      app_id: 'web-search',
      metadata: {
        page: page ?? 'not_found',
        action: 'open_whatsapp_chat',
        cta_id: 'not_found_whatsapp_entry',
        destination: 'whatsapp',
        copy: whatsappButtonLabel,
        experiment_id: experimentId ?? undefined,
        variant: variant ?? undefined,
        channel: 'web_entrypoint',
      },
    }),
    [experimentId, page, variant, whatsappButtonLabel]
  )

  const searchMetricsPayload = useMemo(
    () => ({
      app_id: 'web-search',
      metadata: {
        page: page ?? 'not_found',
        action: 'retry_search',
        cta_id: 'not_found_retry_search',
        destination: 'search',
        copy: SEARCH_BUTTON_LABEL,
        experiment_id: experimentId ?? undefined,
        variant: variant ?? undefined,
        channel: 'web_entrypoint',
      },
    }),
    [experimentId, page, variant]
  )

  useEffect(() => {
    if (isWhatsappCtaOnScreen && !whatsappCtaViewSentRef.current) {
      sendMetrics('SeusDireitos.CtaView', session?.pid?.toString() ?? null, whatsappMetricsPayload)
      whatsappCtaViewSentRef.current = true
    }
  }, [isWhatsappCtaOnScreen, whatsappMetricsPayload, session?.pid])

  useEffect(() => {
    if (isSearchCtaOnScreen && !searchCtaViewSentRef.current) {
      sendMetrics('SeusDireitos.CtaView', session?.pid?.toString() ?? null, searchMetricsPayload)
      searchCtaViewSentRef.current = true
    }
  }, [isSearchCtaOnScreen, searchMetricsPayload, session?.pid])

  const onWhatsAppClick = () => {
    sendMetrics('SeusDireitos.CtaClick', session?.pid?.toString() ?? null, whatsappMetricsPayload)
    const trimmed = searchTerm.trim()
    const prefix = variant ? WHATSAPP_MESSAGE_PREFIX[variant] : undefined
    const message = trimmed ? (prefix ? `${prefix}${trimmed}` : trimmed) : DEFAULT_WHATSAPP_MESSAGE
    window.location.href = `${WHATSAPP_URL}?text=${encodeURIComponent(message)}`
  }

  const onSearchButtonClick = () => {
    sendMetrics('SeusDireitos.CtaClick', session?.pid?.toString() ?? null, searchMetricsPayload)
    onRetrySearch()
  }

  return (
    <Flex direction="column" align="center" className={styles.root}>
      <Container>
        <Row>
          <Col xs={0} md={2} lg={3} />
          <Col xs={12} md={8} lg={6}>
            <div className={styles.cardWrapper}>
              <Card>
                <Flex direction="column" className={styles.content}>
                  <Flex direction="column" gap="6xs">
                    <Heading size="md" className={styles.centeredText}>
                      Nenhum resultado encontrado
                    </Heading>
                    <BodyText size="md" className={styles.centeredText}>
                      Experimente refazer a sua busca ou enviar diretamente no WhatsApp
                    </BodyText>
                  </Flex>

                  <div className={styles.separatorWrapper}>
                    <Separator />
                  </div>

                  <Flex direction="column" gap="2xs">
                    <Flex direction="column" gap="3xs">
                      <Flex align="center" gap="2xs">
                        <span className={styles.richIconDesktop}>
                          <RichIcon size="lg" type="discovery" icon={SearchIcon} />
                        </span>
                        <Flex direction="column">
                          <Heading size="sm">Refazer a sua busca</Heading>
                          <BodyText size="md">
                            Busque pelo nome, CPF, CNPJ ou número do processo
                          </BodyText>
                        </Flex>
                      </Flex>
                      <Button
                        ref={searchRef}
                        kind="tertiary"
                        size="md"
                        leftIcon={<SearchIcon />}
                        block
                        onClick={onSearchButtonClick}
                      >
                        {SEARCH_BUTTON_LABEL}
                      </Button>
                    </Flex>

                    <Flex direction="column" gap="3xs">
                      <Flex align="center" gap="2xs">
                        <span className={styles.richIconDesktop}>
                          <RichIcon size="lg" type="discovery" icon={QuestionAnswerIcon} />
                        </span>
                        <Flex direction="column">
                          <Heading size="sm">Tirar uma dúvida jurídica</Heading>
                          <BodyText size="md">Entenda os seus direitos de forma rápida</BodyText>
                        </Flex>
                      </Flex>
                      {isVariantB && (
                        <div className={styles.messageContainer}>
                          <Caption className={styles.messageText}>Resposta</Caption>
                          <TruncateText value={2} asChild>
                            <BodyText size="md" className={styles.messageText}>
                              {MESSAGE_PREVIEW_TEXT}
                            </BodyText>
                          </TruncateText>
                        </div>
                      )}
                      <Flex direction="column" gap="5xs" align="stretch">
                        <Button
                          ref={whatsappRef}
                          kind="primary"
                          size="md"
                          leftIcon={<WhatsappIcon />}
                          block
                          onClick={onWhatsAppClick}
                        >
                          {whatsappButtonLabel}
                        </Button>
                        <Caption className={styles.centeredText}>
                          Faça perguntas, entenda seus direitos e seja conectado com advogados
                          especializados
                        </Caption>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </div>
          </Col>
          <Col xs={0} md={2} lg={3} />
        </Row>
      </Container>
    </Flex>
  )
}
